import React, { ChangeEvent, FC, memo, useState } from "react";
import dayjs from "dayjs";
import { useAppSelector } from "hooks";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setPage } from "store/slices/commonDocumentsSlise";

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  Button,
  Modal,
  Pagination,
  PaginationItem,
  Stack,
} from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";

import deleteIcon from "../../assets/icons/delete.svg";
import download from "../../assets/icons/download.svg";
import editIcon from "../../assets/icons/hoverEditIcon.svg";
import axiosApi from "../../core/request";
import { ICommonDocument } from "../../models/commonDocument";
import {
  materialReactTableCell,
  materialReactTableHeader,
} from "../../styles/MUIStyles";
import AgGrid from "../AgGrid/AgGrid";
import Guard from "../Guard";

import { DeleteFile } from "./components/DeleteFile";
import { EditFile } from "./components/EditFile";

import styles from "./styles.module.css";

interface DocumentsListProps {
  isCommonDocuments?: boolean;
  documents: any;
  isPersonal?: boolean;
  allowedPermissions?: any[];
  sortConfig?: {
    column: string | null;
    direction: string | null;
  };
  handleColumnClick?: any;
}

export const DocumentsList: FC<DocumentsListProps> = memo(
  ({
    sortConfig,
    handleColumnClick,
    documents,
    isCommonDocuments,
    isPersonal = false,
    allowedPermissions,
  }) => {
    const dispatch = useDispatch();
    const page = useAppSelector((state) => state.commonDocuments.page);
    const pageSize = useAppSelector((state) => state.commonDocuments.pageSize);
    const [isOpenEditFile, setIsOpenEditFile] = useState(false);
    const [isOpenDeleteFile, setIsOpenDeleteFile] = useState(false);
    const [currentDoc, setCurrentDoc] = useState<null | ICommonDocument>(null);

    const openEditFileModal = (docData: any) => {
      setCurrentDoc(docData);
      setIsOpenEditFile(true);
    };

    const openDeleteFileModal = (docData: any) => {
      setCurrentDoc(docData);
      setIsOpenDeleteFile(true);
    };

    const { t } = useTranslation();

    const downloadDocument = async (docData: any, isPersonal: boolean) => {
      try {
        const currentPath = window.location.pathname;
        let apiUrl;

        if (currentPath.includes("common-info")) {
          apiUrl = isPersonal
            ? `/personal-cabinet/documents/my-docs/${docData?.id}/download`
            : `/personal-cabinet/documents/common-docs/${docData?.id}/download`;
        } else {
          apiUrl = `/common-documents/${docData?.id}/download`;
        }

        const response = await axiosApi.get(apiUrl, {
          responseType: "blob",
        });

        const contentDisposition = response.headers["content-disposition"];
        let fileNameWithExtension;

        if (contentDisposition.includes("filename*=UTF-8''")) {
          fileNameWithExtension = decodeURIComponent(
            contentDisposition.split("filename*=UTF-8''")[1].split(";")[0],
          );
        } else {
          fileNameWithExtension = contentDisposition
            .split("filename=")[1]
            .split(";")[0]
            .replace(/"/g, "");
        }
        const href = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = href;
        link.setAttribute("download", decodeURI(fileNameWithExtension));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.log(error);
      }
    };

    const RowComponent = (p: any) => {
      switch (p.colDef.field) {
        case "customActions":
          return (
            <div>
              {!isPersonal && (
                <Guard
                  allowedPermissions={allowedPermissions || []}
                  permissionName="Edit"
                >
                  <img
                    onClick={() => openEditFileModal(p.data)}
                    className={styles.icon}
                    src={editIcon}
                    alt="edit"
                  />
                </Guard>
              )}
              <Guard
                allowedPermissions={allowedPermissions || []}
                permissionName="DownloadFile"
              >
                <img
                  onClick={() => downloadDocument(p.data, isPersonal)}
                  className={styles.icon}
                  src={download}
                  alt="download"
                />
              </Guard>
              <Guard
                allowedPermissions={allowedPermissions || []}
                permissionName="Delete"
              >
                <img
                  onClick={() => openDeleteFileModal(p.data)}
                  className={styles.icon}
                  src={deleteIcon}
                  alt="delete"
                />
              </Guard>
            </div>
          );
        case "name":
          return (
            <Box sx={materialReactTableCell}>
              <div
              // href="#"
              // onClick={() => viewDocument(p.data, currentPath.includes('common-info'))}
              >
                {p.data.name}
              </div>
            </Box>
          );
        case "description":
          return <Box sx={materialReactTableCell}>{p.data.description}</Box>;
        case "originalFileName":
          return (
            <Box sx={materialReactTableCell}>{p.data.originalFileName}</Box>
          );

        case "date":
          return (
            <Box sx={materialReactTableCell}>
              {dayjs(p.data.createdTimeUtc).format("DD.MM.YYYY")}
            </Box>
          );
        default:
          return null;
      }
    };

    const HeadComponent = (p: any) => {
      switch (p.column.colId) {
        case "customActions":
          return (
            <Box>
              <span>{t("data.actions")}</span>
            </Box>
          );
        case "name":
          return (
            <Box
              sx={materialReactTableHeader}
              style={{ cursor: "pointer" }}
              onClick={() => handleColumnClick("name")}
            >
              <span>{t("data.name")}</span>
              {getArrowForSortDirection("name")}
            </Box>
          );
        case "description":
          return (
            <Box>
              <span>{t("data.description")}</span>
            </Box>
          );
        case "originalFileName":
          return (
            <Box>
              <span>{t("data.mainSkill")}</span>
            </Box>
          );
        case "date":
          return (
            <Box
              sx={materialReactTableHeader}
              style={{ cursor: "pointer" }}
              onClick={() => handleColumnClick("date")}
            >
              <span>{t("documents.documentDate")}</span>
              {getArrowForSortDirection("date")}
            </Box>
          );
      }
    };

    const generateColumnDefs = () => {
      const updatedColumnDefs: any[] = [
        {
          headerName: t("documents.documentName"),
          field: "name",
          resizable: true,
          headerComponent: HeadComponent,
          cellRenderer: RowComponent,
          flex: 3,
        },
        {
          headerName: t("projects.documentDescription"),
          field: "description",
          resizable: true,
          cellRenderer: RowComponent,
          flex: 2,
        },
        {
          headerName: t("documents.documentOriginalName"),
          field: "originalFileName",
          resizable: true,
          cellRenderer: RowComponent,
          flex: 2,
        },
        {
          headerName: t("documents.documentDate"),
          field: "date",
          resizable: true,
          headerComponent: HeadComponent,
          cellRenderer: RowComponent,
          flex: 1,
        },
        {
          headerName: t("data.actions"),
          field: "customActions",
          resizable: true,
          cellRenderer: RowComponent,
          flex: 1,
        },
      ];

      return updatedColumnDefs;
    };

    const columnDefs: any[] = generateColumnDefs();

    const handlePageChange = (event: ChangeEvent<unknown>, newPage: number) => {
      console.log(newPage, "newPage");
      dispatch(setPage(newPage));
    };

    const getArrowForSortDirection = (column: string) => {
      if (sortConfig?.column !== column) {
        return null;
      }

      return sortConfig?.direction === "asc" ? (
        <ArrowUpwardIcon sx={{ color: "#605E5C" }} />
      ) : (
        <ArrowDownwardIcon sx={{ color: "#605E5C" }} />
      );
    };

    return (
      <div>
        <AgGrid
          rowData={isCommonDocuments ? documents.items : documents}
          columnDefs={columnDefs}
          activeRow={null}
          height={"65vh"}
          headerHeight={35}
        />
        {isCommonDocuments && (
          <Stack sx={{ width: "500px", margin: "auto" }}>
            <Pagination
              sx={{ margin: ".5rem auto" }}
              count={Math.ceil((documents?.totalCount ?? 0) / pageSize)}
              page={page}
              boundaryCount={1}
              siblingCount={1}
              size="medium"
              onChange={handlePageChange}
              shape="rounded"
              color="primary"
              variant="outlined"
              renderItem={(item) => (
                <PaginationItem component={Button} {...item} />
              )}
            />
          </Stack>
        )}
        <div>
          <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            open={isOpenEditFile}
            onClose={() => setIsOpenEditFile(false)}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
              backdrop: {
                timeout: 500,
              },
            }}
          >
            <Box>
              <EditFile
                isPersonal={isPersonal}
                setIsOpenEditFile={setIsOpenEditFile}
                id={currentDoc?.id || 0}
                currentDoc={currentDoc}
              />
            </Box>
          </Modal>
          <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            open={isOpenDeleteFile}
            onClose={() => setIsOpenDeleteFile(false)}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
              backdrop: {
                timeout: 500,
              },
            }}
          >
            <Box>
              <DeleteFile
                isPersonal={isPersonal}
                setIsOpenDeleteFile={setIsOpenDeleteFile}
                id={currentDoc?.id || 0}
                originalFileName={currentDoc?.originalFileName || ""}
              />
            </Box>
          </Modal>
        </div>
      </div>
    );
  },
);
